import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, TextField, Typogaphy } from 'vnetwork-ui'

export interface ConfirmationOptions {
    open: boolean
    title: string
    description: string
    onConfirm: (reason?: string | null) => Promise<any> | void
    onClose?: () => void
    confirmationValue?: string | null
    useReason?: boolean
    /** Custom content to render before description (e.g., invoice items preview) */
    customContent?: ReactNode
}

interface ConfirmationContextProps {
    setConfirmation: (options: Partial<ConfirmationOptions>) => void
}

const ConfirmationContext = createContext<ConfirmationContextProps | undefined>(
    undefined
)

export const useConfirmation = (): ConfirmationContextProps => {
    const context = useContext(ConfirmationContext)
    if (!context) {
        throw new Error(
            'useConfirmation must be used within a ConfirmationProvider'
        )
    }
    return context
}

interface ConfirmationProviderProps {
    children: ReactNode
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({
    children,
}) => {
    const { t } = useTranslation()
    const [confirmationState, setConfirmationState] =
        useState<ConfirmationOptions>({
            open: false,
            title: '',
            description: '',
            onConfirm: () => {},
            confirmationValue: null,
            useReason: false,
            customContent: null,
        })

    const [confirmationValue, setConfirmationValue] = useState<string | null>(
        null
    )

    const [loading, setLoading] = useState(false)

    const setConfirmation = useCallback(
        (options: Partial<ConfirmationOptions>) => {
            setConfirmationState({
                open: options.open ?? false,
                title: options.title ?? '',
                description: options.description ?? '',
                onConfirm: options.onConfirm ?? (() => {}),
                onClose: options.onClose,
                confirmationValue: options.confirmationValue ?? null,
                useReason: options.useReason ?? false,
                customContent: options.customContent ?? null,
            })
        },
        []
    )

    const confirmationContextValue = useMemo(
        () => ({ setConfirmation }),
        [setConfirmation]
    )

    const handleClose = () => {
        confirmationState?.onClose?.()
        setConfirmationState({
            ...confirmationState,
            open: false,
            onConfirm: () => {},
            onClose: undefined,
            confirmationValue: null,
            useReason: false,
            customContent: null,
        })
    }

    const handleConfirm = async () => {
        if (
            confirmationState?.confirmationValue &&
            confirmationState.confirmationValue !== confirmationValue
        ) {
            toast.error(
                t('value_confirmation_message', {
                    confirmationValue: confirmationState.confirmationValue,
                })
            )
            return
        }

        setLoading(true)

        try {
            if (!confirmationState?.useReason) {
                await confirmationState.onConfirm()
            } else {
                await confirmationState.onConfirm(confirmationValue)
            }
            setConfirmationState({
                ...confirmationState,
                open: false,
                onConfirm: () => {},
                onClose: undefined,
                confirmationValue: null,
            })
            setConfirmationValue(null)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <ConfirmationContext.Provider value={confirmationContextValue}>
            {children}
            {confirmationState.open && (
                <Dialog
                    open={confirmationState?.open}
                    title={confirmationState?.title}
                    onClose={handleClose}
                    actions={
                        <div className="flex items-center gap-2">
                            <Button
                                disabled={loading}
                                onClick={handleClose}
                                variant="outlined"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                disabled={
                                    loading ||
                                    (Boolean(confirmationState.useReason) &&
                                        !confirmationValue) ||
                                    (Boolean(
                                        confirmationState?.confirmationValue
                                    ) &&
                                        confirmationState.confirmationValue !==
                                            confirmationValue)
                                }
                                loading={loading}
                                onClick={handleConfirm}
                            >
                                {t('confirm')}
                            </Button>
                        </div>
                    }
                    className="z-[999]"
                >
                    {confirmationState?.confirmationValue && (
                        <div>
                            <Typogaphy
                                size="small"
                                className="font-normal text-gray-400"
                            >
                                {t('value_confirmation', {
                                    confirmationValue:
                                        confirmationState.confirmationValue,
                                })}
                            </Typogaphy>
                            <div className="mt-2">
                                <TextField
                                    onChange={(e) =>
                                        setConfirmationValue(e.target.value)
                                    }
                                    size="small"
                                    value={confirmationValue ?? ''}
                                />
                            </div>
                        </div>
                    )}
                    {confirmationState?.useReason && (
                        <div>
                            <Typogaphy
                                size="small"
                                className="font-normal text-gray-400"
                            >
                                {t('reason-cancelled', {
                                    confirmationValue:
                                        confirmationState.confirmationValue,
                                })}
                            </Typogaphy>
                            <div className="mt-2">
                                <textarea
                                    className="w-full border border-gray-100 rounded-[8px] px-4 py-2 resize-none outline-none small"
                                    rows={4}
                                    onChange={(e) =>
                                        setConfirmationValue(e.target.value)
                                    }
                                    placeholder={t(
                                        'reason-cancelled-placeholder'
                                    )}
                                    value={confirmationValue ?? ''}
                                />
                            </div>
                        </div>
                    )}
                    {confirmationState?.customContent && (
                        <div className="mt-2 mb-3 overflow-x-auto">
                            {confirmationState.customContent}
                        </div>
                    )}
                    <div className="mt-2">
                        <Typogaphy size="small" className="font-normal">
                            {confirmationState?.description}
                        </Typogaphy>
                    </div>
                </Dialog>
            )}
        </ConfirmationContext.Provider>
    )
}
